import { useCallback, useEffect, useRef, useState } from "react";

const mediaQuery = "(prefers-reduced-motion: no-preference)";
export default function EngineBackdrop({
  experience,
  stageCount = 1,
  onStageChange,
  onLoadingProgress,
  onReady,
}) {
  const host = useRef(null),
    scene = useRef(null),
    readyNotified = useRef(false);
  const [enabled, setEnabled] = useState(
    () => window.matchMedia(mediaQuery).matches,
  );
  const [ready, setReady] = useState(false),
    [failed, setFailed] = useState(false);
  const signalReady = useCallback(() => {
    if (readyNotified.current) return;
    readyNotified.current = true;
    onLoadingProgress?.(1);
    onReady?.();
  }, [onLoadingProgress, onReady]);
  useEffect(() => {
    const media = window.matchMedia(mediaQuery),
      update = () => setEnabled(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  useEffect(() => {
    if (!enabled || failed) return;
    let stopped = false,
      frame = 0,
      lastTime = 0,
      currentProgress = null,
      targetProgress = 0;
    let lastStage = -1;
    const readProgress = () => {
      if (!experience.current) return 0;
      const rect = experience.current.getBoundingClientRect();
      const travel = Math.max(1, rect.height - window.innerHeight);
      return Math.max(0, Math.min(1, -rect.top / travel));
    };
    const updateStage = (progress) => {
      const nextStage = Math.min(stageCount - 1, Math.floor(progress * stageCount));
      if (nextStage !== lastStage) {
        lastStage = nextStage;
        onStageChange?.(nextStage);
      }
    };
    const draw = (time) => {
      frame = 0;
      if (document.hidden) return;
      if (currentProgress === null) currentProgress = targetProgress;
      const difference = targetProgress - currentProgress;
      if (Math.abs(difference) < 0.0004) {
        currentProgress = targetProgress;
      } else {
        const delta = lastTime ? Math.min(50, time - lastTime) : 16;
        currentProgress += difference * (1 - Math.exp(-delta / 72));
        lastTime = time;
        frame = requestAnimationFrame(draw);
      }
      scene.current?.render(currentProgress);
    };
    const schedule = () => {
      if (document.hidden) return;
      targetProgress = readProgress();
      updateStage(targetProgress);
      if (!frame) {
        lastTime = 0;
        frame = requestAnimationFrame(draw);
      }
    };
    schedule();
    onLoadingProgress?.(0.04);
    if (enabled) import("./engineScene")
      .then(async ({ createEngineScene }) => {
        onLoadingProgress?.(0.12);
        const created = await createEngineScene(
          host.current,
          (_, p) => {
            host.current.dataset.progress = p.toFixed(3);
          },
          () => setFailed(true),
          (progress) => onLoadingProgress?.(0.12 + progress * 0.84),
        );
        if (stopped) {
          created?.dispose();
          return;
        }
        scene.current = created;
        if (created) {
          targetProgress = readProgress();
          currentProgress = targetProgress;
          updateStage(targetProgress);
          created.render(currentProgress);
          setReady(true);
          signalReady();
        }
      })
      .catch(() => {
        if (!stopped) setFailed(true);
      });
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    document.addEventListener("visibilitychange", schedule);
    return () => {
      stopped = true;
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      document.removeEventListener("visibilitychange", schedule);
      scene.current?.dispose();
      scene.current = null;
      setReady(false);
    };
  }, [enabled, failed, experience, stageCount, onStageChange, onLoadingProgress, signalReady]);
  const live = enabled && ready && !failed;
  const showPoster = !enabled || failed;
  const state = live ? "is-live" : showPoster ? "is-poster" : "is-loading";
  return (
    <div
      className={`experience-visual ${state}`}
      aria-hidden="true"
    >
      <div className="experience-canvas" ref={host} />
      {showPoster && (
        <img
          src="/images/engine-blender.webp"
          alt=""
          width="1000"
          height="920"
          fetchPriority="high"
          onLoad={signalReady}
        />
      )}
    </div>
  );
}
